'use client';

import { useInfiniteQuery, useQuery, useSuspenseQuery } from '@tanstack/react-query';

import { notificationsService } from '@website/services/notifications.service';

export interface Notification {
  id: string;
  type: string;
  userId: string;
  title: string;
  message: string;
  metadata?: Record<string, any>;
  read: boolean;
  createdAt: number;
  deletedAt?: number;
}

export function useNotifications(
  filter?: { read?: boolean; limit?: number },
  options?: { enablePolling?: boolean; enabled?: boolean }
) {
  const { enablePolling = false, enabled = true } = options || {};

  return useQuery({
    queryKey: ['notifications', filter?.read, filter?.limit],
    queryFn: async () => {
      const response = await notificationsService.listNotifications(filter);
      return response.notifications || [];
    },
    enabled,
    staleTime: 30 * 1000, // 30 seconds
    retry: 2,
    refetchInterval: enablePolling ? 30000 : false, // Only poll when explicitly enabled
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true
  });
}

/**
 * Suspense-enabled version of useNotifications.
 * Use this with React Suspense boundaries for cleaner loading states.
 */
export function useNotificationsSuspense(filter?: { read?: boolean; limit?: number }) {
  return useSuspenseQuery({
    queryKey: ['notifications', filter?.read, filter?.limit],
    queryFn: async () => {
      const response = await notificationsService.listNotifications(filter);
      return response.notifications || [];
    },
    staleTime: 30 * 1000,
    retry: 2,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true
  });
}

/**
 * Paginated notifications hook for the notifications page.
 * Loads pages on demand via `fetchNextPage()` (View More button).
 * The backend returns notifications sorted by `createdAt DESC` (most recent first).
 */
export function useNotificationsPaginated(
  filter?: { read?: boolean; pageSize?: number },
  options?: { enablePolling?: boolean; enabled?: boolean }
) {
  const { enablePolling = false, enabled = true } = options || {};
  const pageSize = filter?.pageSize ?? 20;

  return useInfiniteQuery({
    queryKey: ['notifications', 'paginated', filter?.read, pageSize],
    queryFn: async ({ pageParam }) => {
      const response = await notificationsService.listNotifications({
        read: filter?.read,
        limit: pageSize,
        cursor: pageParam as string | undefined
      });
      return {
        notifications: response.notifications || [],
        cursor: response.cursor as string | undefined
      };
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.cursor,
    enabled,
    staleTime: 30 * 1000,
    retry: 2,
    refetchInterval: enablePolling ? 30000 : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true
  });
}
